/**
 * Inspector section: source + target sample values for the selected
 * field. Reads `mappingResponse.sample_output[target_table]` projected
 * to the field's column. Source samples come from the upstream table's
 * sample output projected to `mapping.source_field`.
 *
 * If preview hasn't materialized yet (`sample_output` empty), shows a
 * skeleton + a one-time "kick preview" affordance via `onMaterialize`.
 * No fabricated values — empty stays empty.
 */
import React, { useEffect, useRef } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import type { FieldMapping } from '../../services/types';

export interface FieldSampleValuesProps {
  target_table: string;
  target_field: string;
  mapping: FieldMapping | null | undefined;
  /** `mappingResponse.sample_output` — keyed by table name. */
  sampleOutput: Record<string, Array<Record<string, unknown>>> | null | undefined;
  /** Optional: kick preview materialization (when sample_output is empty). */
  onMaterialize?: () => void;
  materializing?: boolean;
  /** Max rows to show per column. */
  limit?: number;
}

function projectColumn(
  rows: Array<Record<string, unknown>> | undefined,
  column: string,
  limit: number,
): Array<unknown> {
  if (!rows || rows.length === 0) return [];
  const out: unknown[] = [];
  for (let i = 0; i < rows.length && out.length < limit; i += 1) {
    const v = rows[i]?.[column];
    out.push(v);
  }
  return out;
}

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'string') return v.length > 60 ? `${v.slice(0, 60)}…` : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 80 ? `${s.slice(0, 80)}…` : s;
  } catch {
    return String(v);
  }
}

export const FieldSampleValues: React.FC<FieldSampleValuesProps> = ({
  target_table,
  target_field,
  mapping,
  sampleOutput,
  onMaterialize,
  materializing = false,
  limit = 8,
}) => {
  const targetRows = sampleOutput?.[target_table];
  const targetSamples = projectColumn(targetRows, target_field, limit);

  const sourceTable = mapping?.source_file;
  const sourceField = mapping?.source_field;
  const sourceRows = sourceTable ? sampleOutput?.[sourceTable] : undefined;
  const sourceSamples =
    sourceField && sourceRows ? projectColumn(sourceRows, sourceField, limit) : [];

  // Plan §B: for `set_constant` mappings (no source field) we surface
  // the constant value in the "source" column — mirrors what every
  // target row will receive.
  const operation = mapping?.operation ?? mapping?.transformation_type;
  const isConstant = operation === 'set_constant' || operation === 'constant';
  const constantValue =
    isConstant && mapping?.parameters && 'value' in (mapping.parameters as any)
      ? (mapping.parameters as any).value
      : undefined;
  const isExpression =
    !isConstant && (operation === 'expression' || Boolean(mapping?.transformation));
  const expressionText = isExpression ? mapping?.transformation : undefined;

  const haveAny = targetSamples.length > 0 || sourceSamples.length > 0 || isConstant || isExpression;

  // Plan §G/2: when this section mounts with no samples and a
  // materializer is available, kick it once. Auto-fires per
  // `(target_table, target_field)` so re-selecting a field whose
  // table is already materialized doesn't trigger again.
  const autoFiredKey = useRef<string | null>(null);
  useEffect(() => {
    const key = `${target_table}.${target_field}`;
    if (haveAny) return;
    if (!onMaterialize) return;
    if (materializing) return;
    if (autoFiredKey.current === key) return;
    autoFiredKey.current = key;
    onMaterialize();
  }, [haveAny, onMaterialize, materializing, target_table, target_field]);

  if (!haveAny) {
    // Operator-facing: never surface "materialization" as a concept.
    // The auto-fire effect above kicks preview on mount; the operator
    // sees a brief skeleton, then samples appear. The retry button is
    // shown only if a previous attempt failed (parent passes
    // `materializing=false` after the failure).
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {materializing
            ? 'Loading samples…'
            : onMaterialize
              ? 'Couldn’t load samples for this field.'
              : 'No samples available for this field.'}
        </Typography>
        {onMaterialize && !materializing && autoFiredKey.current && (
          <Button
            size="small"
            variant="text"
            sx={{ mt: 0.5, textTransform: 'none' }}
            onClick={onMaterialize}
          >
            Try again
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          source: {
            isConstant
              ? `constant = ${renderValue(constantValue)}`
              : isExpression
                ? `expression`
                : sourceTable && sourceField
                  ? `${sourceTable}.${sourceField}`
                  : '—'
          }
        </Typography>
        <Box
          component="pre"
          sx={{
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            margin: 0,
            mt: 0.25,
            p: 0.75,
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 0.5,
            bgcolor: 'rgba(0,0,0,0.02)',
            whiteSpace: 'pre-wrap',
            overflowX: 'auto',
          }}
        >
          {isConstant
            ? `(constant value applied to every target row)\n→ ${renderValue(constantValue)}`
            : isExpression
              ? expressionText ?? '(expression)'
              : sourceSamples.length === 0
                ? '—'
                : sourceSamples.map((v, i) => `${i + 1}. ${renderValue(v)}`).join('\n')}
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          target: {target_table}.{target_field}
        </Typography>
        <Box
          component="pre"
          sx={{
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            margin: 0,
            mt: 0.25,
            p: 0.75,
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 0.5,
            bgcolor: 'rgba(0,0,0,0.02)',
            whiteSpace: 'pre-wrap',
            overflowX: 'auto',
          }}
        >
          {targetSamples.length === 0
            ? '—'
            : targetSamples.map((v, i) => `${i + 1}. ${renderValue(v)}`).join('\n')}
        </Box>
      </Box>
    </Stack>
  );
};

export default FieldSampleValues;
